import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

export default function FinancialInsights() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try { setData((await api.get("/dashboard/financial-insights")).data); } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const max = Math.max(...data.per_worker.map((x) => x.ytd), 1);

  return (
    <div className="space-y-5 max-w-6xl" data-testid="financial-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight">Financial Insights</h1>
        <p className="text-muted-foreground">Earnings per support worker · pending payments · YTD billing for {data.year}.</p>
      </header>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="rounded-3xl p-5 border-2" data-testid="card-paid">
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Paid YTD</div>
          <div className="font-display font-black text-4xl text-emerald-700 mt-1">${data.total_paid.toLocaleString()}</div>
        </Card>
        <Card className="rounded-3xl p-5 border-2" data-testid="card-pending">
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Pending payments</div>
          <div className="font-display font-black text-4xl text-coral mt-1">${data.total_pending.toLocaleString()}</div>
        </Card>
        <Card className="rounded-3xl p-5 border-2" data-testid="card-total">
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Total billed</div>
          <div className="font-display font-black text-4xl text-client mt-1">${(data.total_paid + data.total_pending).toLocaleString()}</div>
        </Card>
      </div>

      <Card className="rounded-3xl p-6 border-2" data-testid="per-worker-list">
        <h2 className="font-display font-extrabold text-2xl mb-4">By worker</h2>
        <div className="space-y-3">
          {data.per_worker.map((p) => (
            <div key={p.worker_id} data-testid={`fin-row-${p.worker_id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold">{p.worker_name}</span>
                <span className="font-mono font-bold">${p.ytd.toLocaleString()}</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(p.ytd / max) * 100}%`, background: "linear-gradient(90deg, hsl(var(--client)), hsl(var(--coral)))" }} />
              </div>
              <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                <span>{p.invoice_count} invoices</span>
                <span className="text-emerald-700">Paid ${p.paid.toLocaleString()}</span>
                <span className="text-coral">Pending ${p.pending.toLocaleString()}</span>
              </div>
            </div>
          ))}
          {data.per_worker.length === 0 && <p className="text-sm text-muted-foreground">No workers yet.</p>}
        </div>
      </Card>
    </div>
  );
}
